import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  useFreeTrialContext,
  useFreeTrialPageNavigation,
  FORM_IDS,
} from 'providers/FreeTrialProvider';
import { hasValue, isFunction, isNumeric } from 'swipeclock-frontend';
import {
  useFreeTrialInputHandler,
  useFreeTrialValidateOnStart,
} from 'hooks/useFreeTrialInputHandler';
import { usePostRegistrationMutation } from 'services/freeTrial';
import { useSnackbarForMessage } from 'providers/SnackbarProvider';
import {
  LogoImage,
  FormInputGrid,
  FormInputHeader,
  FormInputFooter,
  FormDivider,
  FirstNameField,
  LastNameField,
  CompanyField,
  NumberOfEmployeesField,
  BusinessEmailField,
  PhoneField,
} from 'features/freeTrial';
import Button from 'components/Button';
import EastIcon from '@mui/icons-material/East';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

const arrayOfIds = [
  FORM_IDS.FIRST_NAME,
  FORM_IDS.LAST_NAME,
  FORM_IDS.COMPANY_NAME,
  FORM_IDS.NUMBER_OF_EMPLOYEES,
  FORM_IDS.BUSINESS_EMAIL,
  FORM_IDS.PHONE_NUMBER,
];

export default function FormPage() {
  const theme = useTheme();
  const [loading, setLoading] = useState(false); // using flag instead of RTK bool due to wanting loader to persist entire event action
  const { announceSnackbar } = useSnackbarForMessage();
  const { state, actions, meta } = useFreeTrialContext();
  const page = meta.pages.FAIL_SAFE_FORM;
  // These get set in the Landing page and passed to the form
  const defaultValues = state?.formBag[page] || {};
  const navigateToPage = useFreeTrialPageNavigation();
  const {
    disabled,
    getError,
    handleBlur,
    handleChange,
    isValid,
    validationRef,
    setDirty,
  } = useFreeTrialInputHandler({
    ids: arrayOfIds,
    validationCallback,
    page,
  });

  const handleError = useHandleError();

  const [postRegistration] = usePostRegistrationMutation();

  // Check if valid on start
  const isValidOnStart = useValidateForm(null, (errorBooleans) => {
    setDirty(errorBooleans);
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isValid()) {
      const values = Object.keys(validationRef.current).reduce(
        (acc, key) => {
          if (key === FORM_IDS.NUMBER_OF_EMPLOYEES) {
            acc[key] = parseInt(validationRef.current[key]?.value, 10);
            return acc;
          }

          if (key === FORM_IDS.PHONE_NUMBER) {
            acc[key] = cleanPhoneNumber(validationRef.current[key]?.value);
            return acc;
          }

          acc[key] = validationRef.current[key]?.value;
          return acc;
        },
        { campaignId: defaultValues?.campaignId ?? 1 }
      ); // Add back the campaignId from the landing page queryParams or default to 1

      try {
        setLoading(true);
        const { registrationToken } = await postRegistration({
          body: values,
        }).unwrap();

        // Pass values to the providers formBag and
        // registrationToken to be used later
        actions.setRegistrationToken(registrationToken);
        actions.setFormValues(values, page);
        actions.setFormValues(
          {
            companyName: values?.companyName ?? '',
            email: values?.email ?? '',
          },
          meta.pages.BASIC_INFO_FORM
        );
        navigateToPage(meta.pages.CHECK_YOUR_EMAIL);
        announceSnackbar(
          "We've received your information. Please check your email for next steps.",
          'success'
        );
      } catch (error) {
        console.log(`%cFormat Page Error`, 'color: #7FFFD4');
        console.log('error', error);
        handleError(error);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <FormInputGrid>
      <FormInputHeader>
        <Stack direction="row" alignItems="center" gap={2} sx={{ pb: 1 }}>
          <LogoImage />
          Free Trial
        </Stack>

        {(isValidOnStart || !disabled) && (
          <span>
            Awesome, you're all set! Click submit below to kickstart your
            WorkforceHub free trial.
          </span>
        )}
        {!isValidOnStart && disabled && (
          <span className="error">
            Almost there! Please complete all required fields to proceed.
          </span>
        )}
      </FormInputHeader>
      <FirstNameField
        error={getError(FORM_IDS.FIRST_NAME)}
        id={FORM_IDS.FIRST_NAME}
        label="First Name"
        maxLength={50}
        onBlur={handleBlur(FORM_IDS.FIRST_NAME)}
        onChange={handleChange(FORM_IDS.FIRST_NAME)}
        placeholder="Enter your first name"
        defaultValue={defaultValues.firstName}
        required
        type="text"
        validationRef={validationRef}
      />

      <LastNameField
        error={getError(FORM_IDS.LAST_NAME)}
        id={FORM_IDS.LAST_NAME}
        label="Last Name"
        maxLength={50}
        onBlur={handleBlur(FORM_IDS.LAST_NAME)}
        onChange={handleChange(FORM_IDS.LAST_NAME)}
        placeholder="Enter your last name"
        defaultValue={defaultValues.lastName}
        required
        type="text"
        validationRef={validationRef}
      />

      <CompanyField
        error={getError(FORM_IDS.COMPANY_NAME)}
        id={FORM_IDS.COMPANY_NAME}
        label="Company Name"
        maxLength={50}
        onBlur={handleBlur(FORM_IDS.COMPANY_NAME)}
        onChange={handleChange(FORM_IDS.COMPANY_NAME)}
        placeholder="Enter your company name"
        defaultValue={defaultValues.companyName}
        required
        type="text"
        validationRef={validationRef}
      />

      <NumberOfEmployeesField
        error={getError(FORM_IDS.NUMBER_OF_EMPLOYEES)}
        id={FORM_IDS.NUMBER_OF_EMPLOYEES}
        label="Number of Employees"
        maxLength={20}
        onBlur={handleBlur(FORM_IDS.NUMBER_OF_EMPLOYEES)}
        onChange={handleChange(FORM_IDS.NUMBER_OF_EMPLOYEES)}
        placeholder="Enter your number of employees"
        defaultValue={defaultValues.numberOfEmployees}
        required
        formatType="integer"
        validationRef={validationRef}
      />

      <BusinessEmailField
        autoComplete="email"
        error={getError(FORM_IDS.BUSINESS_EMAIL)}
        id={FORM_IDS.BUSINESS_EMAIL}
        label="Business Email"
        maxLength={50}
        onBlur={handleBlur(FORM_IDS.BUSINESS_EMAIL)}
        onChange={handleChange(FORM_IDS.BUSINESS_EMAIL)}
        placeholder="Enter your business email"
        defaultValue={defaultValues.email}
        required
        type="email"
        validationRef={validationRef}
      />
      <PhoneField
        autoComplete="tel"
        error={getError(FORM_IDS.PHONE_NUMBER)}
        formatType="phone"
        hint="Example: ( xxx ) xxx - xxxx"
        id={FORM_IDS.PHONE_NUMBER}
        label="Phone Number"
        maxLength={20}
        onBlur={handleBlur(FORM_IDS.PHONE_NUMBER)}
        onChange={handleChange(FORM_IDS.PHONE_NUMBER)}
        placeholder="Enter your phone number"
        validationRef={validationRef}
        defaultValue={defaultValues.phone}
        required
      />
      <FormDivider />
      <FormInputFooter>
        <Typography
          variant="caption"
          sx={{ '& span': { color: theme.palette.primary.main } }}
        >
          <span>*</span> Indicates a required field.
        </Typography>
        <Button
          color="secondary"
          disabled={!isValidOnStart && disabled}
          endIcon={<EastIcon sx={{ mb: '2px', ml: '4px' }} fontSize="small" />}
          loading={loading}
          loadingPosition="end"
          onClick={handleSubmit}
          sx={{ width: 'max-content', height: '48px', marginLeft: 'auto' }}
          variant="contained"
        >
          Submit
        </Button>
      </FormInputFooter>
    </FormInputGrid>
  );
}

const validationCallback = (id, value) => {
  let error = null;

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const phoneRegex = /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;

  switch (id) {
    case FORM_IDS.FIRST_NAME:
      if (!hasValue(value)) {
        error = 'First name is required.';
      } else if (value.length > 50) {
        error = 'First name is too long.';
      }
      break;
    case FORM_IDS.LAST_NAME:
      if (!hasValue(value)) {
        error = 'Last name is required.';
      } else if (value.length > 50) {
        error = 'Last name is too long.';
      }
      break;
    case FORM_IDS.COMPANY_NAME:
      if (!hasValue(value)) {
        error = 'Company name is required.';
      } else if (value.length > 50) {
        error = 'Company name is too long.';
      }
      break;
    case FORM_IDS.NUMBER_OF_EMPLOYEES:
      if (!hasValue(value)) {
        error = 'Number of employees is required.';
      } else if (!isNumeric(value)) {
        error = 'Please enter a valid number of employees.';
      }
      break;
    case FORM_IDS.BUSINESS_EMAIL:
      if (!hasValue(value)) {
        error = 'Email is required.';
      } else if (!emailRegex.test(value)) {
        error = 'Email is invalid.';
      }
      break;
    case FORM_IDS.PHONE_NUMBER:
      if (!hasValue(cleanPhoneNumber(value))) {
        error = 'Phone number is required.';
      }
      if (!phoneRegex.test(cleanPhoneNumber(value))) {
        error = 'Phone number is invalid.';
      }
      break;
    default:
      break;
  }
  return error;
};

// Function to clean up phone number by removing non-digits
const cleanPhoneNumber = (phoneNumber) => phoneNumber?.replace(/\D/g, '');

// This is used to run through validation on form values
// That were populated from the landing page. This updates
// The valid status of the form on the first render.
let setDirty = false;
export const useValidateForm = (values, callback) => {
  const { meta } = useFreeTrialContext();
  const page = meta.pages.FAIL_SAFE_FORM;

  const { valid, errors } = useFreeTrialValidateOnStart({
    ids: arrayOfIds,
    validationCallback,
    page,
    values,
  });

  // We want to update all errors to dirty so validation happens on change events now.
  // errors are an object of type { id, page, validationError }
  // we only want to return the validationError as a boolean instead of a string
  // so we can use it to set the dirty state of the input
  if (!setDirty && isFunction(callback)) {
    const errorBooleans = errors.reduce((acc, obj) => {
      acc[obj.id] = !!obj.validationError;
      return acc;
    }, {});

    callback(errorBooleans);
    setDirty = true;
  }

  return valid;
};

// Used in the catch of the submit try/catch
// We broke this out to help readability
// This is used to handle errors from the API
// and direct the user to the correct page if needed
// or back to the form to fix errors.
const useHandleError = () => {
  const { meta } = useFreeTrialContext();
  const { announceSnackbar } = useSnackbarForMessage();
  const navigateToPage = useFreeTrialPageNavigation();
  const errorHandlers = {
    400: (apiError) => {
      if (apiError?.data?.title === 'EmailAlreadyExists') {
        navigateToPage(meta.pages.ERROR_EMAIL_ALREADY_EXISTS);
        announceSnackbar(
          'We found your email in an existing WorkforceHub company.',
          'error'
        );
      } else if (apiError?.data?.title === 'SwipeclockEmail') {
        navigateToPage(meta.pages.ERROR_SWIPECLOCK_EMAIL);
        announceSnackbar(
          'Swipeclock email addresses cannot be used to set up sites via this process.',
          'error'
        );
      } else {
        announceSnackbar(
          'Almost there! Please complete all required fields to proceed.',
          'error'
        );
      }
    },
    default: () => {
      navigateToPage(meta.pages.ERROR_DEFAULT);
      announceSnackbar(
        `Something went wrong. Please contact support for assistance.`,
        'error'
      );
    },
  };

  return (error) => {
    const handler = errorHandlers[error.status] || errorHandlers.default;
    handler(error);
  };
};
